
/**
 * ClientService.js contains all of the service functions used for client configuration.
 *
 * @endpoints /client/
 * 	retrieve - retrieve the configuration
 *  template/retrieve - retrieve the configuration template
 *
 * @author Cameron Bulock <cameron.bulock@quavo.com>
 */

import API from '@/services/QFDAPIService';

export default {
    name: 'ClientService',
    /**
     * List clients
     * @param {Object} params
     */
    list({ TenantId, TemplatesOnly }, dispatch) {
        return API.call('/client/list', {
            TenantId, TemplatesOnly
        }).catch(error => {
            if (error.message) {
                dispatch("notifications/addNotification", {
                    status: 'error',
                    message: 'A ' + error.message + ' has occurred. Please refresh the page or try again later.',
                });
            } else {
                dispatch("notifications/addNotification", {
                    status: 'error',
                    message: 'An error has occurred. Please refresh the page or try again later.',
                });
            }
        })
    },

    /**
     * List client changes
     * @param {Object} params
     */
    changeList({ ClientId, TenantId }, dispatch) {
        return API.call('/client/change/list', {
            ClientId, TenantId,
        }).catch(error => {
            if (error.message) {
                dispatch("notifications/addNotification", {
                    status: 'error',
                    message: 'A ' + error.message + ' has occurred. Please refresh the page or try again later.',
                });
            } else {
                dispatch("notifications/addNotification", {
                    status: 'error',
                    message: 'An error has occurred. Please refresh the page or try again later.',
                });
            }
        })
    },

    /**
     * Edit client change
     * @param {Object} params
     */
    editChange({ ClientId, TenantId, ChangeTitle, ChangeDetails, Version }, dispatch) {
        return API.call('/client/change/edit', {
            ClientId, TenantId, ChangeTitle, ChangeDetails, Version
        }).catch(error => {
            if (error.message) {
                dispatch("notifications/addNotification", {
                    status: 'error',
                    message: 'A ' + error.message + ' has occurred. Please refresh the page or try again later.',
                });
            } else {
                dispatch("notifications/addNotification", {
                    status: 'error',
                    message: 'An error has occurred. Please refresh the page or try again later.',
                });
            }
        })
    },

    /**
     * Restore client change
     * @param {Object} params
     */
    restoreChange({ ClientId, ClientInfoRuleId, TenantId, ChangeTitle, ChangeDetails, Version }, dispatch) {
        return API.call('/client/change/restore', {
            ClientId, ClientInfoRuleId, TenantId, ChangeTitle, ChangeDetails, Version
        }).catch(error => {
            if (error.message) {
                dispatch("notifications/addNotification", {
                    status: 'error',
                    message: 'A ' + error.message + ' has occurred. Please refresh the page or try again later.',
                });
            } else {
                dispatch("notifications/addNotification", {
                    status: 'error',
                    message: 'An error has occurred. Please refresh the page or try again later.',
                });
            }
        })
    },

    /**
     * Create/update client configuration
     * @param {Object} params
     */
    save({ ClientIdList, ClientInfoRule, TenantId, ChangeTitle, ChangeDetails, ArrayMergeOption }, dispatch) {
        return API.call('/client/create', {
            ClientIdList, TenantId, ChangeTitle, ChangeDetails, ClientInfoRule, ArrayMergeOption
        }).catch(error => {
            if (error.message) {
                dispatch("notifications/addNotification", {
                    status: 'error',
                    message: 'A ' + error.message + ' has occurred. Please refresh the page or try again later.',
                });
            } else {
                dispatch("notifications/addNotification", {
                    status: 'error',
                    message: 'An error has occurred. Please refresh the page or try again later.',
                });
            }
        })
    },

    /**
     * Retrieve the client configuration data
     * @param {Object} params
     * @returns an object containing configuration values
     */
    retrieve({ ClientInfoRuleId, ClientId, TenantId }, dispatch) {
        return API.call('/client/retrieve', {
            TenantId, ClientId, ClientInfoRuleId
        }).catch(error => {
            if (error.message) {
                dispatch("notifications/addNotification", {
                    status: 'error',
                    message: 'A ' + error.message + ' has occurred. Please refresh the page or try again later.',
                });
            } else {
                dispatch("notifications/addNotification", {
                    status: 'error',
                    message: 'An error has occurred. Please refresh the page or try again later.',
                });
            }
        })
    },

    /**
     * Retrieve the client configuration template
     * @param {Object} params
     * @returns an object containing configuration template values
     */
    retrieveTemplate({ TenantId }, dispatch) {
        return API.call('/client/template/retrieve', {
            TenantId, ClientId: "Default"
        }).catch(error => {
            if (error.message) {
                dispatch("notifications/addNotification", {
                    status: 'error',
                    message: 'A ' + error.message + ' has occurred. Please refresh the page or try again later.',
                });
            } else {
                dispatch("notifications/addNotification", {
                    status: 'error',
                    message: 'An error has occurred. Please refresh the page or try again later.',
                });
            }
        })
    },

    /**
     * Promote config
     */
    promote(promoteRequest, dispatch) {
        return API.call('/client/change/promote', promoteRequest).catch(error => {
            if (error.message) {
                dispatch("notifications/addNotification", {
                    status: 'error',
                    message: 'A ' + error.message + ' has occurred. Please refresh the page or try again later.',
                });
            } else {
                dispatch("notifications/addNotification", {
                    status: 'error',
                    message: 'An error has occurred. Please refresh the page or try again later.',
                });
            }
        })
    },


    /**
     * Create/update client configuration in QFD
     * @param {Object} params
     */
    saveToQFD({ ClientIdList, ClientInfoRule, ClientInfoRuleId, TenantId, ChangeTitle, ChangeDetails }, dispatch) {
        return API.call('/QFDConfig/v1/save', {
            ClientIdList, TenantId, ConfigLabel: "Dev Testing", ConfigPurpose: "Dev Testing", ClientInfoRule, ClientInfoRuleId, ChangeTitle, ChangeDetails
        }, "QFD").catch(error => {
            if (error.message) {
                dispatch("notifications/addNotification", {
                    status: 'error',
                    message: 'A ' + error.message + ' has occurred. Please refresh the page or try again later.',
                });
            } else {
                dispatch("notifications/addNotification", {
                    status: 'error',
                    message: 'An error has occurred. Please refresh the page or try again later.',
                });
            }
        })
    },

    /**
     * Retrieve client configuration from QFD
     * @param {Object} params
     */
    retrieveFromQFD({ ClientId }, dispatch) {
        return API.call('/QFDConfig/v1/retrieve', {
            ClientId
        }, "QFD").catch(error => {
            if (error.message) {
                dispatch("notifications/addNotification", {
                    status: 'error',
                    message: 'A ' + error.message + ' has occurred. Please refresh the page or try again later.',
                });
            } else {
                dispatch("notifications/addNotification", {
                    status: 'error',
                    message: 'An error has occurred. Please refresh the page or try again later.',
                });
            }
        })
    },

}
